import { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  collection,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  type DocumentData,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '@/lib/firebase';
import type {
  AccountDoc,
  AccountJobDoc,
  AnalysisDoc,
  CallDoc,
} from '@/lib/types';
import { ScoreChip } from '@/components/ScoreChip';
import { DealTableView } from '@/components/DealTableView';
import { QAPanel } from '@/components/QAPanel';

interface CallRow extends CallDoc {
  id: string;
}

interface ActiveJob {
  jobId: string;
  kind: 'pulling' | 'reanalyze';
  doc?: AccountJobDoc;
}

export function AccountDetailPage() {
  const { accountId } = useParams<{ accountId: string }>();
  const [account, setAccount] = useState<AccountDoc | null>(null);
  const [analysis, setAnalysis] = useState<AnalysisDoc | null>(null);
  const [calls, setCalls] = useState<CallRow[]>([]);
  const [activeJob, setActiveJob] = useState<ActiveJob | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [enqueuing, setEnqueuing] = useState<'pulling' | 'reanalyze' | null>(null);

  useEffect(() => {
    if (!accountId) return;
    const unsubAccount = onSnapshot(doc(db, 'accounts', accountId), (snap) => {
      setAccount((snap.data() as AccountDoc | undefined) ?? null);
    });
    const callsQ = query(
      collection(db, 'accounts', accountId, 'calls'),
      orderBy('date', 'desc'),
    );
    const unsubCalls = onSnapshot(callsQ, (snap) => {
      setCalls(
        snap.docs.map((d) => ({ id: d.id, ...(d.data() as DocumentData) }) as CallRow),
      );
    });
    const analysesQ = query(
      collection(db, 'accounts', accountId, 'analyses'),
      orderBy('createdAt', 'desc'),
      limit(1),
    );
    const unsubAnalysis = onSnapshot(analysesQ, (snap) => {
      const first = snap.docs[0];
      setAnalysis(first ? ((first.data() as AnalysisDoc) ?? null) : null);
    });
    return () => {
      unsubAccount();
      unsubCalls();
      unsubAnalysis();
    };
  }, [accountId]);

  // Subscribe to the active job doc (if any) so we can show progress and
  // clear the spinner once it transitions to done/failed.
  useEffect(() => {
    if (!accountId || !activeJob) return;
    const ref = doc(db, 'accounts', accountId, 'jobs', activeJob.jobId);
    const unsub = onSnapshot(ref, (snap) => {
      const data = snap.data() as AccountJobDoc | undefined;
      if (!data) return;
      setActiveJob((cur) =>
        cur && cur.jobId === activeJob.jobId ? { ...cur, doc: data } : cur,
      );
      if (data.status === 'done') {
        setActiveJob(null);
        setError(null);
      } else if (data.status === 'failed') {
        setError(data.error ?? 'Job failed');
        setActiveJob(null);
      }
    });
    return () => unsub();
  }, [accountId, activeJob?.jobId]);

  const startJob = async (kind: 'pulling' | 'reanalyze') => {
    if (!accountId) return;
    setEnqueuing(kind);
    setError(null);
    try {
      const fn = httpsCallable<
        { accountId: string; pullNew?: boolean; force?: boolean },
        { jobId: string; status: 'queued' }
      >(functions, 'analyzeAccountNow');
      const payload =
        kind === 'pulling'
          ? { accountId, pullNew: true }
          : { accountId };
      const res = await fn(payload);
      setActiveJob({ jobId: res.data.jobId, kind });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setEnqueuing(null);
    }
  };

  const busy = enqueuing ?? activeJob?.kind ?? null;
  const jobStatus = activeJob?.doc?.status;
  const jobLabel = (() => {
    if (enqueuing === 'pulling') return 'Queuing…';
    if (enqueuing === 'reanalyze') return 'Queuing…';
    if (activeJob?.kind === 'pulling') {
      if (jobStatus === 'queued') return 'Queued — waiting to pull…';
      if (jobStatus === 'running') return 'Pulling new calls + analyzing…';
      return 'Pulling…';
    }
    if (activeJob?.kind === 'reanalyze') {
      if (jobStatus === 'queued') return 'Queued — waiting for worker…';
      if (jobStatus === 'running') return 'Re-analyzing account…';
      return 'Re-analyzing…';
    }
    return null;
  })();

  const headerScores = useMemo(
    () => ({
      momentum: analysis?.momentum ?? account?.momentum,
      strength: analysis?.strength ?? account?.strength,
    }),
    [analysis, account],
  );

  if (!accountId) return null;
  if (!account) {
    return <div className="text-slate-500">Loading account…</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <Link to="/" className="text-xs text-slate-500 hover:underline">
            &larr; All accounts
          </Link>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight">
            {account.name}
          </h1>
          <div className="mt-2 flex gap-2">
            <ScoreChip score={headerScores.momentum} kind="momentum" />
            <ScoreChip score={headerScores.strength} kind="strength" />
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => startJob('pulling')}
            disabled={!!busy}
            className="btn-secondary"
            title="Pull recently recorded Gong calls and re-synthesize the account"
          >
            {busy === 'pulling' ? 'Pulling…' : 'Pull new calls'}
          </button>
          <button
            onClick={() => startJob('reanalyze')}
            disabled={!!busy}
            className="btn-primary"
            title="Re-run the synthesis using existing calls (uses cached per-call analyses)"
          >
            {busy === 'reanalyze' ? 'Re-analyzing…' : 'Re-analyze'}
          </button>
        </div>
      </div>

      {jobLabel && (
        <div className="card flex items-center gap-3 border-sky-200 bg-sky-50 p-3 text-sm text-sky-900">
          <span
            className="inline-block h-3 w-3 animate-pulse rounded-full bg-sky-500"
            aria-hidden
          />
          <span>{jobLabel}</span>
          <span className="ml-auto text-xs text-sky-700/80">
            Safe to leave this page — results will appear automatically.
          </span>
        </div>
      )}

      {error && (
        <div className="card border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          {analysis && (
            <div className="card p-5">
              <h2 className="label mb-2">Executive summary</h2>
              <p className="whitespace-pre-line text-sm text-slate-800">
                {analysis.executiveSummary}
              </p>
              <div className="mt-3 grid grid-cols-1 gap-3 text-xs text-slate-500 sm:grid-cols-2">
                <div>
                  <span className="font-semibold text-slate-700">Momentum:</span>{' '}
                  {analysis.momentumRationale}
                </div>
                <div>
                  <span className="font-semibold text-slate-700">Strength:</span>{' '}
                  {analysis.strengthRationale}
                </div>
              </div>
            </div>
          )}

          <DealTableView analysis={analysis} />

          <div className="card overflow-hidden">
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
              <h2 className="text-sm font-semibold">
                Calls ({calls.length})
              </h2>
            </div>
            <ul className="divide-y divide-slate-100">
              {calls.length === 0 && (
                <li className="px-4 py-6 text-sm text-slate-500">
                  No calls ingested yet.
                </li>
              )}
              {calls.map((c) => (
                <li key={c.id} className="px-4 py-3 text-sm">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-slate-800">
                        {c.title || 'Untitled call'}
                      </div>
                      <div className="text-xs text-slate-500">
                        {c.date?.toDate().toLocaleString() ?? '—'} ·{' '}
                        {c.participants?.length ?? 0} participants
                      </div>
                    </div>
                    <div className="text-xs text-slate-500">
                      {c.perCallAnalysis ? 'analyzed' : 'pending'}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="lg:col-span-1">
          <QAPanel accountId={accountId} />
        </div>
      </div>
    </div>
  );
}
